"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import InlineBanner from "@/components/ui/inline-banner";
import { Spinner } from "@/components/ui/spinner";
import FeedCard, { type FeedItem } from "./FeedCard";
import ImageLightbox from "@/components/chat/ImageLightbox";
import { apiFetch } from "@/lib/api";

type FeedResponse = { items?: FeedItem[]; nextCursor?: string | null } | FeedItem[];

function isArray<T>(x: any): x is T[] { return Array.isArray(x); }

export default function UnifiedFeed({ personId, take = 12 }: { personId: string | number; take?: number }) {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [cursor, setCursor] = useState<string | null>("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  const fetchPage = useCallback(async (cur: string | null) => {
    // apiFetch, not bare fetch (2026-07-24): this renders the OWNER's "Your
    // content" section, and an unauthenticated request made the feed apply
    // guest gating — blurred titles and _blurred thumbnail URLs (which don't
    // even exist for v2 video thumbs) on the user's own profile.
    const qs = new URLSearchParams({ personId: String(personId) });
    if (cur) qs.set("cursor", cur);
    if (take) qs.set("take", String(take));
    // Explicit /api/proxy path: '/api/feed' sits in api.ts's Next-handled
    // prefix list but NO route handler exists for it, so the bare path
    // dead-ends at the Next server (503 in prod, 2026-07-24). The proxy path
    // resolves same-origin under every build config and forwards auth.
    const res = await apiFetch(`/api/proxy/api/feed?${qs.toString()}`, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = (await res.json()) as FeedResponse;
    const arr = isArray<FeedItem>(json) ? json : (isArray<FeedItem>((json as any)?.items) ? (json as any).items : []);
    const next = (json as any)?.nextCursor ?? null;
    return { arr, next } as { arr: FeedItem[]; next: string | null };
  }, [personId, take]);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems([]);
    setCursor("");
    setHasMore(true);
    (async () => {
      try {
        const { arr, next } = await fetchPage("");
        if (cancelled) return;
        setItems(arr);
        setCursor(next);
        setHasMore(Boolean(arr.length && (next || arr.length >= take)));
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load feed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchPage, take]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(async (entries) => {
      const first = entries[0];
      if (!first?.isIntersecting) return;
      if (loadingMore) return;
      setLoadingMore(true);
      try {
        const { arr, next } = await fetchPage(cursor);
        setItems((prev) => [...prev, ...arr]);
        setCursor(next);
        setHasMore(Boolean(arr.length && (next || arr.length >= take)));
      } catch (e: any) {
        setError(e?.message || "Failed to load more");
      } finally {
        setLoadingMore(false);
      }
    }, { rootMargin: "400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [cursor, hasMore, loadingMore, fetchPage, take]);

  const openImage = useCallback((src: string) => setLightboxImage(src), []);
  const closeImage = useCallback(() => setLightboxImage(null), []);

  return (
    <section className="space-y-4">
      {/* Lightbox for images */}
      <ImageLightbox src={lightboxImage || undefined} open={Boolean(lightboxImage)} onClose={closeImage} />

      {loading ? (
        <div>
          <p className="sr-only" aria-live="polite">Loading feed…</p>
          <div aria-hidden="true" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: Math.max(6, take) }).map((_, i) => (
              <div key={i} className="border rounded-lg overflow-hidden">
                <div className="aspect-video skeleton" />
                <div className="p-3 space-y-2">
                  <div className="h-4 w-3/4 skeleton rounded" />
                  <div className="h-3 w-1/3 skeleton rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : error ? null : items.length === 0 ? null : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((it, idx) => (
              <FeedCard key={`${(it as any).id ?? idx}-${idx}`} item={it} onOpenImage={openImage} />
            ))}
          </div>
          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-10" />
          {loadingMore && (
            <div className="flex justify-center py-3"><Spinner label="Loading more." /></div>
          )}
        </>
      )}
    </section>
  );
}
